import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    description_tr: '',
    description_en: '',
    requirements_tr: '',
    requirements_en: '',
    location: '',
    type: 'full-time',
    active: true,
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (error.response?.status === 401) {
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.id}`, formData);
        toast.success('İlan güncellendi!');
      } else {
        await axios.post(`${API}/jobs`, formData);
        toast.success('İlan eklendi!');
      }
      await fetchJobs();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving job:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401) {
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else if (error.response?.status === 429) {
        toast.error(detail || 'Çok fazla istek. Lütfen biraz bekleyin.');
      } else if (detail) {
        toast.error(`Hata: ${detail}`);
      } else if (error.message) {
        toast.error(`Bağlantı hatası: ${error.message}`);
      } else {
        toast.error('Bir hata oluştu!');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ilanı silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast.success('İlan silindi!');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401) {
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else if (detail) {
        toast.error(`Hata: ${detail}`);
      } else {
        toast.error('Bir hata oluştu!');
      }
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title_tr: job.title_tr,
      title_en: job.title_en,
      description_tr: job.description_tr,
      description_en: job.description_en,
      requirements_tr: job.requirements_tr,
      requirements_en: job.requirements_en,
      location: job.location,
      type: job.type,
      active: job.active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingJob(null);
    setFormData({
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      requirements_tr: '',
      requirements_en: '',
      location: '',
      type: 'full-time',
      active: true,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, active: checked });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="jobs-admin-title"
          >
            Kariyer İlanları Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-job-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni İlan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingJob ? 'İlan Düzenle' : 'Yeni İlan Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title_tr">Başlık (TR)</Label>
                    <Input
                      id="title_tr"
                      name="title_tr"
                      value={formData.title_tr}
                      onChange={handleChange}
                      required
                      data-testid="job-title-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title_en">Title (EN)</Label>
                    <Input
                      id="title_en"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      required
                      data-testid="job-title-en"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description_tr">Açıklama (TR)</Label>
                  <Textarea
                    id="description_tr"
                    name="description_tr"
                    value={formData.description_tr}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="job-desc-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="description_en">Description (EN)</Label>
                  <Textarea
                    id="description_en"
                    name="description_en"
                    value={formData.description_en}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="job-desc-en"
                  />
                </div>
                <div>
                  <Label htmlFor="requirements_tr">Gereksinimler (TR)</Label>
                  <Textarea
                    id="requirements_tr"
                    name="requirements_tr"
                    value={formData.requirements_tr}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="job-req-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="requirements_en">Requirements (EN)</Label>
                  <Textarea
                    id="requirements_en"
                    name="requirements_en"
                    value={formData.requirements_en}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="job-req-en"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Lokasyon</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      data-testid="job-location"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Çalışma Tipi</Label>
                    <Select value={formData.type} onValueChange={(value) => handleSelectChange('type', value)}>
                      <SelectTrigger data-testid="job-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full-time">Tam Zamanlı</SelectItem>
                        <SelectItem value="part-time">Yarı Zamanlı</SelectItem>
                        <SelectItem value="contract">Sözleşmeli</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={handleSwitchChange}
                    data-testid="job-active"
                  />
                  <Label htmlFor="active">Aktif İlan</Label>
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="job-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`job-card-${job.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{job.title_tr}</h3>
                    {job.active && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Aktif</span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-2">{job.description_tr}</p>
                  <div className="text-sm text-gray-500">
                    <span>{job.location}</span> • <span>{job.type}</span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(job)} data-testid={`edit-job-${job.id}`}>
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(job.id)} data-testid={`delete-job-${job.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Sil
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminJobs;